/* eslint-disable no-param-reassign */
import {ChangeEvent} from 'react';
import Image from 'next/image';
import {Box, Button, CircularProgress, makeStyles} from '@material-ui/core';
import {useImmer} from 'use-immer';
import CancelIcon from '@material-ui/icons/Cancel';
import {Document} from '../../APIs/CustomerPortalAPI';
import {useDocumentMutation} from './hooks/useDocumentMutation';
import RootStore from '../../Core/mobx/RootStore';
import {useS3UploadMutation} from './hooks/useS3UploadMutation';

interface UploadedDocumentProps {
    value: Document;
    onRemove: () => void;
}

const useStyles = makeStyles(() => ({
    wrapper: {
        display: 'flex',
        gap: '10px',
        '&:hover .remove-btn': {
            display: 'inline-block',
        },
    },
    uploadButton: {
        display: 'inline-flex',
        alignItems: 'center',
        height: '42px',
    },
    filenameWrapper: {
        flex: 1,
        display: 'flex',
        position: 'relative',
        alignItems: 'center',
    },
    filename: {
        color: '#8e8e8e',
    },
    icon: {
        maxHeight: '42px',
    },
    removeButton: {
        display: 'none',
        cursor: 'pointer',
        borderRadius: '10rem',
        marginTop: '-34px',
        background: 'transparent',
        color: '#c4c4c4',
        height: '18px',
        width: '18px',
        fontSize: '18px',
        padding: 0,
        margin: 0,
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
    },
}));

function UploadedDocument({value, onRemove}: UploadedDocumentProps) {
    const classes = useStyles();
    return (
        <Box className={classes.wrapper}>
            <Image alt="PDF Icon" src="/img/pdf-file.png" className={classes.icon} width={21} height={24} />
            <Box className={classes.filenameWrapper}>
                <a href={value.downloadUrl} className={classes.filename}>
                    {value.filename}
                </a>
                <button onClick={onRemove} className={`remove-btn ${classes.removeButton}`} type="button">
                    <CancelIcon style={{fontSize: '18px'}} />
                </button>
            </Box>
        </Box>
    );
}

export const PolicyDocument = {
    POLICY_INFORMATION: 'Policy Information',
    TERMS_AND_CONDITIONS: 'Terms & Conditions',
    ILLUSTRATION: 'Illustration',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PolicyDocument = (typeof PolicyDocument)[keyof typeof PolicyDocument];

interface PolicyFileUploadProps {
    id: string;
    name: PolicyDocument;
    s3keyGenerator: (file: File) => string;
    value?: Document;
    onChange: (value: Document | null) => void;
}

type LocalState = {
    value?: Document | null;
    isDirty: boolean;
    isUploading: boolean;
};

function PolicyFileUpload({id, value, name, s3keyGenerator, onChange}: PolicyFileUploadProps) {
    const classes = useStyles();
    const [state, setState] = useImmer<LocalState>({
        value,
        isDirty: false,
        isUploading: false,
    });

    const {mutateAsync: mutate} = useDocumentMutation();
    const {mutateAsync: uploadToS3} = useS3UploadMutation();

    const onUpload = async (file: File) => {
        setState((draft) => {
            draft.isUploading = true;
        });
        try {
            const s3key = encodeURI(s3keyGenerator(file));
            const doc = await mutate({
                name,
                filename: file.name,
                s3key,
            });

            await uploadToS3({url: doc.uploadUrl, file});
            setState((draft) => {
                draft.isDirty = true;
                draft.value = doc;
                draft.isUploading = false;
            });
            onChange(doc);
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Unknown error';
            RootStore.uiStore.showSnackbar(`Document upload failed: ${message}`, 'error');
            setState((draft) => {
                draft.isUploading = false;
            });
        }
    };

    const onRemove = () => {
        if (state.value) {
            setState((draft) => {
                draft.isDirty = true;
                draft.value = null;
            });
            onChange(null);
        }
    };

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) onUpload(file);
    };

    return state.value ? (
        <UploadedDocument value={state.value} onRemove={onRemove} />
    ) : (
        <label htmlFor={id} className={classes.uploadButton}>
            {!state.isUploading && (
                <input accept=".pdf" style={{display: 'none'}} id={id} onChange={handleFileChange} type="file" />
            )}
            <Button variant="outlined" color="primary" component="span">
                {state.isUploading ? (
                    <>
                        <CircularProgress size={16} />
                        <span>Uploading...</span>
                    </>
                ) : (
                    'Add File'
                )}
            </Button>
        </label>
    );
}

export default PolicyFileUpload;
